import React, {CSSProperties, useContext} from 'react'
import {Util} from 'yougile-util'
import {LoaderIcon} from '@components/common/base/loader-new'
import {usePopstateListener} from '@components/common/popup/use-popstate-listener'
import {Overlay} from '@components/overlay'
import {noPropagate, useGlobalKeyDown} from '@containers/hooks'
import {TwFooter, TwMoveToBody, TwWnd} from './containers'
import {popupCloseSubscriptions, PropsContext} from './context'
import {PopupContent} from './popup-content'
import {PopupHeader} from './popup-header'
import {PopupProps} from './types'
import {Container} from './utils'

export const Popup = (props: PopupProps) => {
  return (
    <PropsContext.Provider value={props}>
      <PopupBase />
    </PropsContext.Provider>
  )
}

export const PopupBase = () => {
  const props = useContext(PropsContext)
  const {
    type = 'popup',
    title,
    close,
    footer,
    sidebar,
    width,
    className,
    autoHeight = false,
    closeOnOverlayDisabled = false,
    minHeightAuto = false,
    height,
    noPropagateOverlay = false,
    noPropagateBody = false,
    popupInWnd = false,
    minHeight,
    fixedPosition = false,
    scrollBarRef,
    containerRef,
    isLoading = false,
    rounded = '8',
    paddings,
    headerPaddings,
    contentPaddings,
    footerPaddings,
    dividerVisibility = 'auto',
    noCross = false,
    autoHeightHeader = false,
    inlineDisplay = false,
    subTitle,
    headerStyle,
    onCloseBySubs,
    overlay,
    zIndex
  } = props

  const closeWithSubs = (): boolean | void => {
    if (close) {
      close()
      onCloseBySubs?.()
      Object.values(popupCloseSubscriptions).forEach(f => f())
      return true
    }
  }

  useGlobalKeyDown({escape: closeWithSubs})
  usePopstateListener(closeWithSubs)

  const style: CSSProperties = {width}
  if (minHeight) {
    style.minHeight = minHeight
  }
  if (height) {
    style.height = height
  }

  const content = (
    <Container
      onClose={e => {
        noPropagateOverlay && noPropagate(e)
        closeOnOverlayDisabled ? Util.noop : closeWithSubs()
      }}
      popupInWnd={popupInWnd}
      inlineDisplay={inlineDisplay}
      fixedPosition={fixedPosition}
      overlay={overlay}
      zIndex={zIndex}
    >
      <TwWnd
        onClick={e => noPropagateBody && noPropagate(e)}
        $autoHeight={autoHeight}
        $height={height}
        $minHeightAuto={minHeightAuto}
        style={style}
        $rounded={rounded}
      >
        {isLoading && (
          <Overlay position="absolute" bgColor="opacity-gray-40">
            <LoaderIcon />
          </Overlay>
        )}
        <PopupHeader
          paddings={headerPaddings ?? paddings}
          onClose={closeWithSubs}
          title={title}
          subTitle={subTitle}
          type={type}
          noCross={noCross}
          autoHeightHeader={autoHeightHeader}
          headerStyle={headerStyle}
        />
        <PopupContent
          dividerVisibility={dividerVisibility}
          sidebar={sidebar}
          paddings={contentPaddings ?? paddings}
          scrollBarRef={scrollBarRef}
          containerRef={containerRef}
        />
        {!!footer && (
          <TwFooter
            $paddings={type === 'dialog' ? 0 : footerPaddings ?? paddings}
          >
            {footer}
          </TwFooter>
        )}
      </TwWnd>
    </Container>
  )

  if (popupInWnd || inlineDisplay) {
    return <>{content}</>
  }

  return <TwMoveToBody className={className}>{content}</TwMoveToBody>
}
